import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { contentApi } from '../../api/content';
import { PlusIcon, TrashIcon, DocumentIcon, ArrowUpTrayIcon, XMarkIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import * as XLSX from 'xlsx';

const CATEGORIAS = [
	{ value: 'servicios', label: 'Servicios' },
	{ value: 'comercio', label: 'Comercio' },
	{ value: 'construccion', label: 'Construcción' },
	{ value: 'inmobiliaria', label: 'Inmobiliaria' },
	{ value: 'agro', label: 'Agrocomercio' },
	{ value: 'otro', label: 'Otro' },
];

const EMPTY_ROW = {
	id: '',
	nombre_empresa: '',
	fecha_constitucion: '',
	actividad: '',
	ubicacion: '',
	costo: 0,
	cuenta_bancaria: false,
	rpe: false,
	deudas_tributarias: false,
	saldo_favor_itbis: false,
	saldo_favor_ir2: false,
	descripcion: '',
	resumen: '',
	categoria: 'servicios',
	incluye: [],
	activa: true,
	en_carusel: true,
};

export default function EmpresasCarpetaAdmin() {
	const fileInputRef = useRef(null);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');
	const [ok, setOk] = useState('');
	const [data, setData] = useState({
		items: [],
		carousel: { enabled: true, title: 'Empresas en carpeta', subtitle: '', max_items: 8 },
	});

	const [docConteos, setDocConteos] = useState({});
	const [incluyeModal, setIncluyeModal] = useState(null); // { idx, nombre_empresa, text }

	const openIncluyeModal = (item, idx) => {
		setIncluyeModal({
			idx,
			nombre_empresa: item.nombre_empresa || 'Sin nombre',
			text: Array.isArray(item.incluye) ? item.incluye.join('\n') : '',
		});
	};
	const saveIncluye = () => {
		if (!incluyeModal) return;
		const lines = incluyeModal.text.split('\n').map((s) => s.trim()).filter(Boolean);
		updateItem(incluyeModal.idx, 'incluye', lines);
		setIncluyeModal(null);
	};

	const refreshDocConteos = useCallback(async () => {
		try {
			const res = await contentApi.getDocumentosConteos();
			setDocConteos(res || {});
		} catch { /* silenciar */ }
	}, []);

	useEffect(() => {
		(async () => {
			try {
				setLoading(true);
				const resp = await contentApi.getEmpresasCarpetaData();
				setData({
					items: Array.isArray(resp?.items) ? resp.items : [],
					carousel: {
						enabled: !!resp?.carousel?.enabled,
						title: resp?.carousel?.title || 'Empresas en carpeta',
						subtitle: resp?.carousel?.subtitle || '',
						max_items: Number(resp?.carousel?.max_items || 8),
					},
				});
				refreshDocConteos();
			} catch (e) {
				setError(e?.response?.data?.detail || e.message || 'Error cargando empresas en carpeta');
			} finally {
				setLoading(false);
			}
		})();
	}, [refreshDocConteos]);

	const totalActivas = useMemo(
		() => data.items.filter((i) => i && i.activa).length,
		[data.items]
	);

	const updateItem = (idx, field, value) => {
		setData((prev) => {
			const items = [...prev.items];
			items[idx] = { ...items[idx], [field]: value };
			return { ...prev, items };
		});
	};

	const addItem = () => {
		setData((prev) => ({
			...prev,
			items: [...prev.items, { ...EMPTY_ROW, id: Date.now() }],
		}));
	};

	const removeItem = (idx) => {
		setData((prev) => ({
			...prev,
			items: prev.items.filter((_, i) => i !== idx),
		}));
	};

	const handleSave = async () => {
		try {
			setSaving(true);
			setError('');
			setOk('');
			const payload = {
				items: data.items.map((i, idx) => ({
					...i,
					id: i.id || idx + 1,
					costo: Number(i.costo || 0),
				})),
				carousel: {
					enabled: !!data.carousel.enabled,
					title: data.carousel.title || 'Empresas en carpeta',
					subtitle: data.carousel.subtitle || '',
					max_items: Number(data.carousel.max_items || 8),
				},
			};
			await contentApi.updateEmpresasCarpetaData(payload);
			setOk('Cambios guardados correctamente.');
			setTimeout(() => setOk(''), 3000);
		} catch (e) {
			setError(e?.response?.data?.detail || e.message || 'Error guardando cambios');
		} finally {
			setSaving(false);
		}
	};

	const handleDownloadTemplate = () => {
		const headers = [[
			'id',
			'nombre_empresa',
			'fecha_constitucion',
			'actividad',
			'ubicacion',
			'costo',
			'cuenta_bancaria',
			'rpe',
			'deudas_tributarias',
			'saldo_favor_itbis',
			'saldo_favor_ir2',
			'descripcion',
			'resumen',
			'categoria',
			'incluye',
			'activa',
			'en_carusel',
		]];
		const example = [[
			1,
			'VARVION, S.R.L.',
			'2026-03-25',
			'Servicios multiples y consultoria empresarial',
			'Alma Rosa, Santo Domingo Este',
			30000,
			'SI',
			'SI',
			'SI',
			'SI',
			'SI',
			'Empresa con documentacion al dia',
			'Comercializacion de productos en general',
			'comercio',
			'Cambio de socios;Sello empresa;Acceso DGII',
			'SI',
			'SI',
		]];
		const ws = XLSX.utils.aoa_to_sheet([...headers, ...example]);
		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, ws, 'plantilla');
		XLSX.writeFile(wb, 'plantilla_empresas_carpeta.xlsx');
	};

	const toBool = (v) => {
		const s = String(v ?? '').trim().toLowerCase();
		return s === '1' || s === 'true' || s === 'si' || s === 'sí' || s === 'yes';
	};

	const handleImportExcel = async (event) => {
		const file = event.target.files?.[0];
		if (!file) return;
		try {
			setError('');
			const buf = await file.arrayBuffer();
			const wb = XLSX.read(buf, { type: 'array' });
			const ws = wb.Sheets[wb.SheetNames[0]];
			const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
			const imported = rows
				.map((r, idx) => ({
					id: Number(r.id || idx + 1),
					nombre_empresa: String(r.nombre_empresa || '').trim(),
					fecha_constitucion: String(r.fecha_constitucion || '').trim(),
					actividad: String(r.actividad || '').trim(),
					ubicacion: String(r.ubicacion || '').trim(),
					costo: Number(r.costo || 0),
					cuenta_bancaria: toBool(r.cuenta_bancaria),
					rpe: toBool(r.rpe),
					deudas_tributarias: toBool(r.deudas_tributarias),
					saldo_favor_itbis: toBool(r.saldo_favor_itbis),
					saldo_favor_ir2: toBool(r.saldo_favor_ir2),
					descripcion: String(r.descripcion || '').trim(),
					resumen: String(r.resumen || '').trim(),
					categoria: String(r.categoria || 'servicios').trim(),
					incluye: String(r.incluye || '').split(';').map((s) => s.trim()).filter(Boolean),
					activa: r.activa === '' ? true : toBool(r.activa),
					en_carusel: r.en_carusel === '' ? true : toBool(r.en_carusel),
				}))
				.filter((r) => r.nombre_empresa);
			setData((p) => ({ ...p, items: imported }));
			setOk(`Importadas ${imported.length} filas desde Excel.`);
			setTimeout(() => setOk(''), 3500);
		} catch (e) {
			setError('No se pudo importar el archivo. Verifica que use la plantilla.');
		} finally {
			if (fileInputRef.current) fileInputRef.current.value = '';
		}
	};

	// --- Documentos ---
	const [docModal, setDocModal] = useState(null); // { empresaId, nombreEmpresa }
	const [docs, setDocs] = useState([]);
	const [docsLoading, setDocsLoading] = useState(false);
	const [uploading, setUploading] = useState(false);
	const docFileRef = useRef(null);
	const [newDoc, setNewDoc] = useState({ titulo: '', tipo: 'otro', descripcion: '', publico: true });

	const TIPO_CHOICES = [
		{ value: 'estado_financiero', label: 'Estado Financiero' },
		{ value: 'certificacion', label: 'Certificación' },
		{ value: 'registro_mercantil', label: 'Registro Mercantil' },
		{ value: 'dgii', label: 'Documento DGII' },
		{ value: 'legal', label: 'Documento Legal' },
		{ value: 'otro', label: 'Otro' },
	];

	const loadDocs = useCallback(async (empresaId) => {
		try {
			setDocsLoading(true);
			const res = await contentApi.getDocumentosEmpresaAdmin(empresaId);
			setDocs(Array.isArray(res) ? res : []);
		} catch {
			setDocs([]);
		} finally {
			setDocsLoading(false);
			refreshDocConteos();
		}
	}, [refreshDocConteos]);

	const openDocModal = (item) => {
		const empId = item.id || 0;
		setDocModal({ empresaId: empId, nombreEmpresa: item.nombre_empresa || 'Sin nombre' });
		setNewDoc({ titulo: '', tipo: 'otro', descripcion: '', publico: true });
		loadDocs(empId);
	};

	const handleUploadDoc = async () => {
		const file = docFileRef.current?.files?.[0];
		if (!file || !newDoc.titulo.trim()) {
			window.alert('Debes seleccionar un archivo y escribir un título.');
			return;
		}
		try {
			setUploading(true);
			const fd = new FormData();
			fd.append('archivo', file);
			fd.append('empresa_carpeta_id', docModal.empresaId);
			fd.append('titulo', newDoc.titulo.trim());
			fd.append('tipo', newDoc.tipo);
			fd.append('descripcion', newDoc.descripcion.trim());
			fd.append('publico', newDoc.publico);
			await contentApi.subirDocumentoEmpresa(fd);
			setNewDoc({ titulo: '', tipo: 'otro', descripcion: '', publico: true });
			if (docFileRef.current) docFileRef.current.value = '';
			loadDocs(docModal.empresaId);
		} catch (e) {
			window.alert(e?.response?.data?.detail || e.message || 'Error subiendo documento');
		} finally {
			setUploading(false);
		}
	};

	const handleDeleteDoc = async (docId) => {
		if (!window.confirm('¿Eliminar este documento?')) return;
		try {
			await contentApi.eliminarDocumentoEmpresa(docId);
			loadDocs(docModal.empresaId);
		} catch (e) {
			window.alert('Error eliminando documento');
		}
	};

	const handleTogglePublico = async (doc) => {
		try {
			await contentApi.actualizarDocumentoEmpresa(doc.id, { publico: !doc.publico });
			loadDocs(docModal.empresaId);
		} catch {
			window.alert('Error actualizando visibilidad');
		}
	};

	if (loading) {
		return <div className="p-6 text-gray-600">Cargando empresas en carpeta...</div>;
	}

	return (
		<div className="space-y-6 p-4 md:p-6">
			<div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 md:p-6">
				<h1 className="text-2xl font-semibold text-gray-900">Gestión de Empresas en Carpeta</h1>
				<p className="text-sm text-gray-600 mt-1">
					Administra el listado completo y qué empresas se muestran en el carrusel de la página principal.
				</p>
				<div className="mt-3 text-sm text-gray-700">Activas: <strong>{totalActivas}</strong> / {data.items.length}</div>
			</div>

			<div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 md:p-6 space-y-4">
				<h2 className="text-lg font-semibold text-gray-900">Configuración de carrusel</h2>
				<div className="flex flex-col lg:flex-row lg:items-center gap-3">
					<label className="flex items-center gap-2 text-sm text-gray-700">
						<input
							type="checkbox"
							checked={data.carousel.enabled}
							onChange={(e) => setData((p) => ({ ...p, carousel: { ...p.carousel, enabled: e.target.checked } }))}
						/>
						Habilitado
					</label>
					<input
						className="lg:w-64 px-3 py-2 border border-gray-300 rounded-lg text-sm"
						placeholder="Título"
						value={data.carousel.title}
						onChange={(e) => setData((p) => ({ ...p, carousel: { ...p.carousel, title: e.target.value } }))}
					/>
					<input
						className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
						placeholder="Subtítulo"
						value={data.carousel.subtitle}
						onChange={(e) => setData((p) => ({ ...p, carousel: { ...p.carousel, subtitle: e.target.value } }))}
					/>
					<input
						type="number"
						min={1}
						max={20}
						className="lg:w-28 px-3 py-2 border border-gray-300 rounded-lg text-sm"
						placeholder="Máximo"
						value={data.carousel.max_items}
						onChange={(e) => setData((p) => ({ ...p, carousel: { ...p.carousel, max_items: e.target.value } }))}
					/>
				</div>
			</div>

			<div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 md:p-6">
				<div className="flex items-center justify-between mb-4">
					<h2 className="text-lg font-semibold text-gray-900">Empresas</h2>
					<div className="flex items-center gap-2">
						<input
							ref={fileInputRef}
							type="file"
							accept=".xlsx,.xls"
							className="hidden"
							onChange={handleImportExcel}
						/>
						<button onClick={handleDownloadTemplate} className="px-3 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm hover:bg-slate-50">
							Descargar plantilla
						</button>
						<button onClick={() => fileInputRef.current?.click()} className="px-3 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm hover:bg-slate-50">
							Importar Excel
						</button>
						<button onClick={addItem} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-primary text-white text-sm">
							<PlusIcon className="h-4 w-4" />
							Agregar fila
						</button>
					</div>
				</div>
				<div className="overflow-x-auto">
					<table className="min-w-[1900px] w-full text-sm">
						<thead>
							<tr className="bg-slate-100 text-slate-700">
								<th className="p-2 text-left">Nombre</th>
								<th className="p-2 text-left">Fecha</th>
								<th className="p-2 text-left">Categoría</th>
								<th className="p-2 text-left">Resumen</th>
								<th className="p-2 text-left">Actividad</th>
								<th className="p-2 text-left">Ubicación</th>
								<th className="p-2 text-left">Costo (DOP)</th>
								<th className="p-2 text-center">C. Bancaria</th>
								<th className="p-2 text-center">RPE</th>
								<th className="p-2 text-center">Deuda Trib.</th>
								<th className="p-2 text-center">ITBIS</th>
								<th className="p-2 text-center">IR-2</th>
								<th className="p-2 text-center">Activa</th>
								<th className="p-2 text-center">Carrusel</th>
								<th className="p-2 text-center">Incluye</th>
								<th className="p-2 text-center">Docs</th>
								<th className="p-2 text-right">Acción</th>
							</tr>
						</thead>
						<tbody>
							{data.items.map((item, idx) => (
								<tr key={`${item.id || idx}`} className="border-b">
									<td className="p-2"><input className="w-full border rounded px-2 py-1" value={item.nombre_empresa || ''} onChange={(e) => updateItem(idx, 'nombre_empresa', e.target.value)} /></td>
									<td className="p-2"><input type="date" className="w-full border rounded px-2 py-1" value={item.fecha_constitucion || ''} onChange={(e) => updateItem(idx, 'fecha_constitucion', e.target.value)} /></td>
									<td className="p-2">
										<select className="w-full border rounded px-2 py-1 text-sm" value={item.categoria || 'servicios'} onChange={(e) => updateItem(idx, 'categoria', e.target.value)}>
											{CATEGORIAS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
										</select>
									</td>
									<td className="p-2"><textarea className="w-full border rounded px-2 py-1 text-sm" rows={2} placeholder="Resumen breve para la card" value={item.resumen || ''} onChange={(e) => updateItem(idx, 'resumen', e.target.value)} /></td>
									<td className="p-2"><textarea className="w-full border rounded px-2 py-1" rows={2} value={item.actividad || ''} onChange={(e) => updateItem(idx, 'actividad', e.target.value)} /></td>
									<td className="p-2"><input className="w-full border rounded px-2 py-1" value={item.ubicacion || ''} onChange={(e) => updateItem(idx, 'ubicacion', e.target.value)} /></td>
									<td className="p-2"><input type="number" className="w-full border rounded px-2 py-1" value={item.costo || 0} onChange={(e) => updateItem(idx, 'costo', e.target.value)} /></td>
									<td className="p-2 text-center"><input type="checkbox" checked={!!item.cuenta_bancaria} onChange={(e) => updateItem(idx, 'cuenta_bancaria', e.target.checked)} /></td>
									<td className="p-2 text-center"><input type="checkbox" checked={!!item.rpe} onChange={(e) => updateItem(idx, 'rpe', e.target.checked)} /></td>
									<td className="p-2 text-center"><input type="checkbox" checked={!!item.deudas_tributarias} onChange={(e) => updateItem(idx, 'deudas_tributarias', e.target.checked)} /></td>
									<td className="p-2 text-center"><input type="checkbox" checked={!!item.saldo_favor_itbis} onChange={(e) => updateItem(idx, 'saldo_favor_itbis', e.target.checked)} /></td>
									<td className="p-2 text-center"><input type="checkbox" checked={!!item.saldo_favor_ir2} onChange={(e) => updateItem(idx, 'saldo_favor_ir2', e.target.checked)} /></td>
									<td className="p-2 text-center"><input type="checkbox" checked={!!item.activa} onChange={(e) => updateItem(idx, 'activa', e.target.checked)} /></td>
									<td className="p-2 text-center"><input type="checkbox" checked={!!item.en_carusel} onChange={(e) => updateItem(idx, 'en_carusel', e.target.checked)} /></td>
									<td className="p-2 text-center">
										{(() => {
											const cnt = item.incluye?.length || 0;
											return (
												<button
													onClick={() => openIncluyeModal(item, idx)}
													className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${cnt > 0 ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' : 'text-slate-500 hover:bg-slate-100'}`}
													title={cnt > 0 ? `${cnt} ítem(s)` : 'Sin ítems'}
												>
													{cnt > 0 ? `Incluye (${cnt})` : 'Incluye'}
												</button>
											);
										})()}
									</td>
									<td className="p-2 text-center">
										{(() => {
											const cnt = docConteos[item.id] || 0;
											const hasDocs = cnt > 0;
											return (
												<button
													onClick={() => openDocModal(item)}
													className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
														hasDocs
															? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
															: 'text-primary hover:bg-primary/10'
													}`}
													title={hasDocs ? `${cnt} documento(s)` : 'Sin documentos'}
												>
													<DocumentIcon className="h-4 w-4" />
													{hasDocs ? `Docs (${cnt})` : 'Docs'}
												</button>
											);
										})()}
									</td>
									<td className="p-2 text-right">
										<button onClick={() => removeItem(idx)} className="inline-flex items-center gap-1 px-2 py-1 text-red-600 hover:bg-red-50 rounded">
											<TrashIcon className="h-4 w-4" />
											Eliminar
										</button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>

			{error && <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}
			{ok && <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm">{ok}</div>}

			<div className="flex justify-end">
				<button
					onClick={handleSave}
					disabled={saving}
					className="px-4 py-2 rounded-lg bg-primary text-white disabled:opacity-50"
				>
					{saving ? 'Guardando...' : 'Guardar cambios'}
				</button>
			</div>

			{/* Modal Incluye */}
			{incluyeModal && (
				<div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setIncluyeModal(null)}>
					<div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
						<div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
							<div>
								<h3 className="text-base font-semibold text-gray-900">Ítems incluidos</h3>
								<p className="text-sm text-gray-500">{incluyeModal.nombre_empresa}</p>
							</div>
							<button onClick={() => setIncluyeModal(null)} className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100">
								<XMarkIcon className="h-5 w-5" />
							</button>
						</div>
						<div className="px-6 py-4 space-y-3">
							<p className="text-xs text-gray-500">Escribe un ítem por línea. Se mostrará como lista en la página pública.</p>
							<textarea
								className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
								rows={10}
								placeholder={'Cambio de socios\nDesignación de Gerente\nAcceso Oficina Virtual DGII\n...'}
								value={incluyeModal.text}
								onChange={(e) => setIncluyeModal((p) => ({ ...p, text: e.target.value }))}
							/>
							<div className="text-xs text-gray-400">{incluyeModal.text.split('\n').filter((s) => s.trim()).length} ítems</div>
						</div>
						<div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
							<button onClick={() => setIncluyeModal(null)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50">
								Cancelar
							</button>
							<button onClick={saveIncluye} className="px-4 py-2 bg-primary text-white rounded-lg text-sm">
								Guardar ítems
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Modal documentos */}
			{docModal && (
				<div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setDocModal(null)}>
					<div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
						<div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
							<div>
								<h3 className="text-lg font-semibold text-gray-900">Documentos</h3>
								<p className="text-sm text-gray-500">{docModal.nombreEmpresa}</p>
							</div>
							<button onClick={() => setDocModal(null)} className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100">
								<XMarkIcon className="h-5 w-5" />
							</button>
						</div>

						{/* Subir nuevo */}
						<div className="px-6 py-4 bg-gray-50 border-b border-gray-200 space-y-3">
							<p className="text-sm font-semibold text-gray-700">Subir documento</p>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
								<input
									className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
									placeholder="Título del documento *"
									value={newDoc.titulo}
									onChange={(e) => setNewDoc((p) => ({ ...p, titulo: e.target.value }))}
								/>
								<select
									className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
									value={newDoc.tipo}
									onChange={(e) => setNewDoc((p) => ({ ...p, tipo: e.target.value }))}
								>
									{TIPO_CHOICES.map((t) => (
										<option key={t.value} value={t.value}>{t.label}</option>
									))}
								</select>
							</div>
							<input
								className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
								placeholder="Descripción (opcional)"
								value={newDoc.descripcion}
								onChange={(e) => setNewDoc((p) => ({ ...p, descripcion: e.target.value }))}
							/>
							<div className="flex items-center gap-4">
								<input ref={docFileRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" className="text-sm" />
								<label className="flex items-center gap-2 text-sm text-gray-700">
									<input
										type="checkbox"
										checked={newDoc.publico}
										onChange={(e) => setNewDoc((p) => ({ ...p, publico: e.target.checked }))}
									/>
									Visible públicamente
								</label>
							</div>
							<button
								onClick={handleUploadDoc}
								disabled={uploading}
								className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white text-sm disabled:opacity-50"
							>
								<ArrowUpTrayIcon className="h-4 w-4" />
								{uploading ? 'Subiendo...' : 'Subir documento'}
							</button>
						</div>

						{/* Lista de documentos */}
						<div className="px-6 py-4">
							{docsLoading ? (
								<p className="text-sm text-gray-500 py-4 text-center">Cargando documentos...</p>
							) : docs.length === 0 ? (
								<p className="text-sm text-gray-500 py-4 text-center">No hay documentos para esta empresa.</p>
							) : (
								<div className="space-y-2">
									{docs.map((doc) => (
										<div key={doc.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50">
											<div className="flex items-center gap-3 min-w-0">
												<DocumentIcon className="h-5 w-5 text-primary flex-shrink-0" />
												<div className="min-w-0">
													<p className="text-sm font-medium text-gray-900 truncate">{doc.titulo}</p>
													<p className="text-xs text-gray-500">
														{doc.tipo_display} · {doc.nombre_original || 'archivo'} · {new Date(doc.fecha_subida).toLocaleDateString('es-DO')}
													</p>
													{doc.descripcion && <p className="text-xs text-gray-400 mt-0.5 truncate">{doc.descripcion}</p>}
												</div>
											</div>
											<div className="flex items-center gap-2 flex-shrink-0">
												<button
													onClick={() => handleTogglePublico(doc)}
													className={`p-1.5 rounded ${doc.publico ? 'text-emerald-600 hover:bg-emerald-50' : 'text-gray-400 hover:bg-gray-100'}`}
													title={doc.publico ? 'Visible públicamente (clic para ocultar)' : 'Oculto (clic para hacer público)'}
												>
													{doc.publico ? <EyeIcon className="h-4 w-4" /> : <EyeSlashIcon className="h-4 w-4" />}
												</button>
												{doc.archivo_url && (
													<a href={doc.archivo_url} target="_blank" rel="noopener noreferrer" className="px-2 py-1 text-xs text-primary hover:bg-primary/10 rounded font-medium">
														Descargar
													</a>
												)}
												<button onClick={() => handleDeleteDoc(doc.id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded">
													<TrashIcon className="h-4 w-4" />
												</button>
											</div>
										</div>
									))}
								</div>
							)}
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

